import React, { useState, useEffect, useRef, useMemo } from 'react';
import styles from "../MarkSubmission/MarksEntryPage.module.css";
import { useSubject } from '../../context/SubjectContext';
import MarkSubmissionHeader from '../../components/MarkSubmission/MarkSubmissionHeader';
import UpdateMarkSubmissionTable from '../../components/UpdateMarkSubmission/UpdateMarkSubmissionTable';
import { useApi } from "../../context/ApiClient";
import { useCenter } from '../../context/CenterContext';
import { useConsortiaCenter } from '../../context/ConsortiaCenterContext';
import { useLocation } from "react-router-dom";
import {
  MARK_STATUS,
  AUTO_SAVE_DELAY_MS
} from "../../constants/Constants";
import { Toaster } from "react-hot-toast";
import { getQualificationTitle, getCenterType , checkunitIsConsortiaUnit} from '../../utils/utils';

const UpdateMarksEntryPage = () => {
  const { subjectData , subjects} = useSubject();
  const { selectedSubject, selectedSubjectCode, subjectId } = subjectData || {};
  const [candidates, setCandidates] = useState([]);
  const [availableAssignments, setAvailableAssignments] = useState([]);
  const { centerNumber } = useCenter();
  const location = useLocation();
  const { unit, sessionId, rows: passedRows, isStage3, isPostStage3 } = location.state || {};
  const autoSaveTimeoutRef = useRef(null);
   const { consortiaCentres = [] } = useConsortiaCenter();
  const currentCentreType = getCenterType(consortiaCentres, Number(centerNumber), subjectData?.selectedSubject, subjectData?.selectedSubjectId) || "";
  const unitcode = String(unit).split(" ")[0].trim().toUpperCase();
  
    const isConsortiaUnit = checkunitIsConsortiaUnit(
      consortiaCentres,
      centerNumber,
      sessionId,
      unitcode,
    );
  

  const [showModal, setShowModal] = useState(false);
  // Snapshot of last successfully saved rows (serialized)
  const lastSavedRowsRef = useRef(JSON.stringify([]));
  const [lastSavedAt, setLastSavedAt] = useState(null);

  
    const [errorVisible, setErrorVisible] = useState(false);
 
  const api = useApi(process.env.REACT_APP_AMS_BASE_URL);

  // Derive a unique storage key so different units/sessions/centres don't collide
  const unitCode = useMemo(() => (unit ? String(unit).split(" ")[0] : ""), [unit]);
  const STORAGE_KEY = useMemo(() => {
    if (!centerNumber || !sessionId || !unitCode) return null;
    return `updateMarks:${centerNumber}:${sessionId}:${unitCode}`;
  }, [centerNumber, sessionId, unitCode]);

  const isRowComplete = (row) => {
    if (row.markAsAbsent || row.disableCandidate) return true;

    const marks = row.totalCriteriaAchieved;
    const hasMarks =
      marks !== null &&
      marks !== undefined &&
      !(typeof marks === "string" && marks.trim() === "");

    if (!hasMarks) return false;

    const assignment = row.assignmentId;
    const hasAssignment =
      assignment !== null &&
      assignment !== undefined &&
      !(typeof assignment === "string" && assignment.trim() === "");

    if (!hasAssignment) return false;

    const num = Number(marks);
    if (Number.isNaN(num)) return false;

    const max = Number(row.totalMarks);
    if (Number.isNaN(max)) return false;

    if (num < 0 || num > max) return false;

    if (typeof marks === "string" && /^0\d+$/.test(marks)) return false;

    return true;
  };

  // Initialize with passedRows if provided; otherwise empty and we’ll populate after fetch/localStorage
  const [rows, setRows] = useState(() => {
    if (Array.isArray(passedRows) && passedRows.length) return passedRows;
    return [];
  });
   const anyRowInvalid = useMemo(
      () => rows.some((row) => !isRowComplete(row)),
      [rows]
    );
    // Submit click handler
      const handleOcrSubmit = () => {
        if (anyRowInvalid) {
          setErrorVisible(true);
          setShowModal(false);
        } else {
          setErrorVisible(false);
          setShowModal(true);
        }
      };
    
      // Auto-hide error banner if everything becomes valid later
      useEffect(() => {
        if (errorVisible && !anyRowInvalid) {
          setErrorVisible(false);
        }
      }, [anyRowInvalid, errorVisible]);
  // Fetch candidates + base rows from API, then prefer localStorage if present (and matches)
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const res = await api.get(
          currentCentreType === "Lead" && isConsortiaUnit
            ? "consortia/getConsortiaMarkSubmissionCandidateData"
            : "getMarkSubmissionCandidateData",
          {
            params: {
              centreNumber: centerNumber,
              sessionId: sessionId,
              assessmentCode: unitCode,
            },
          }
        );
        if (res.data.isSuccess) {
          const apiData = res.data.data?.candidates || [];
          const assignments = res.data.data?.availableAssignments || [];

          setAvailableAssignments(assignments);

          

          const candidateList = apiData.map((item) => ({
           candidateNumber:
              currentCentreType === "Lead" && isConsortiaUnit
                ? String(item.centreCandidateNumber)
                : String(item.candidateNumber),
            ...(currentCentreType === "Lead" &&
              isConsortiaUnit && { centreNumber: item.centreNumber }),
            candidateName: item.candidateName,
            isSampled: item.isSampled,
            markStatus: item.markStatus,
            markAsAbsent: item.markAsAbsent || false,
            disableCandidate: item.disableCandidate || false,
          }));
          setCandidates(candidateList);

          const rowList = apiData.map((item) => ({
            candidateNumber:
              currentCentreType === "Lead" && isConsortiaUnit
                ? String(item.centreCandidateNumber)
                : String(item.candidateNumber),
            ...(currentCentreType === "Lead" &&
              isConsortiaUnit && { centreNumber: item.centreNumber }),
            candidateName: item.candidateName,
            isSampled: item.isSampled,
            markStatus: item.markStatus,
            markAsAbsent: item.markAsAbsent || false,
            totalCriteriaAchieved: item.totalCriteriaAchieved ?? null,
            totalMarks: item.totalMarks ?? "",
            assignmentId: item.assignmentId ?? "",
            assignment: assignments.find(
              (a) => String(a.assignmentId) === String(item.assignmentId ?? "")
            )?.assignmentLabel || "",
            disableCandidate:item.disableCandidate || false,
            existingTotalCriteriaScore: item.existingTotalCriteriaScore === 0 ? 0 : item.existingTotalCriteriaScore || "",
          }));

          // If rows were passed in navigation, keep them and set the snapshot accordingly
          if (Array.isArray(passedRows) && passedRows.length) {
            lastSavedRowsRef.current = JSON.stringify(passedRows);
            return;
          }

          // Try restore from localStorage if available and matches candidateNumbers
          let restored = null;
          if (STORAGE_KEY) {
            try {
              const stored = localStorage.getItem(STORAGE_KEY);
              if (stored) {
                const parsed = JSON.parse(stored);
                const matches =
                  Array.isArray(parsed) &&
                  parsed.length === candidateList.length &&
                  candidateList.every((c, i) => parsed[i]?.candidateNumber === c.candidateNumber);
                if (matches) {
                  restored = parsed;
                }
              }
            } catch {
              // ignore malformed storage
            }
          }

          const nextRows = restored || rowList;
          setRows(nextRows);
          lastSavedRowsRef.current = JSON.stringify(nextRows);
        }
      } catch (err) {
        console.error("Error fetching candidate data", err);
      }
    };

    if (centerNumber && sessionId && unitCode) {
      fetchCandidates();
    }
  }, [api, centerNumber, sessionId, unitCode, passedRows, STORAGE_KEY]);

  // Persist to localStorage on every rows change (UI-sticky regardless of server)
  useEffect(() => {
    if (!STORAGE_KEY) return;
    try {
      if (rows && rows.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
      }
    } catch (e) {
      console.warn("Failed to persist rows to localStorage:", e);
    }
  }, [rows, STORAGE_KEY]);

  // Build payload identical to manual save
  const buildSavePayload = () => {
    const unitCodeLocal = unitCode;
    return rows.map((row) => ({
      candidateNumber: Number(row.candidateNumber),
      markAsAbsent: Boolean(row.markAsAbsent),
      totalCriteriaAchieved: row.totalCriteriaAchieved ?? null,
      assignmentId: row.assignmentId || null,
      markStatusId: MARK_STATUS.SAVED,
      centreNumber: Number(centerNumber),
      sessionId: sessionId,
      subjectId: Number(subjectId),
      assessmentCode: unitCodeLocal,
    }));
  };

  // Silent auto-save (no toast)
  const handleAutoSave = async () => {
    const currentSerialized = JSON.stringify(rows);
    if (currentSerialized === lastSavedRowsRef.current) return;
    try {
      const payload = buildSavePayload();
      await api.post(`saveOrUpdateCandidateMarks`, payload);
      lastSavedRowsRef.current = currentSerialized;
      setLastSavedAt(new Date());
      // Keep also a non-namespaced copy if you want, but STORAGE_KEY is the one we restore from
      try {
        localStorage.setItem("marksRows", currentSerialized);
      } catch {}
    } catch (e) {
      console.error("Auto-save failed", e);
    }
  };

  // Start a timer at the FIRST unsaved change, do NOT reset it on further edits.
   const serialized = JSON.stringify(rows);
    const hasUnsavedChanges = serialized !== lastSavedRowsRef.current;

  useEffect(() => {
   
    if (hasUnsavedChanges) {
      if (!autoSaveTimeoutRef.current) {
        autoSaveTimeoutRef.current = setTimeout(() => {
          handleAutoSave();
          autoSaveTimeoutRef.current = null; // allow new cycle after next edit
        }, AUTO_SAVE_DELAY_MS);
      }
    } else {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    }
  }, [rows]);

  // Cleanup on unmount (if user navigates away before timer fires)
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, []);

  // Attempt final synchronous save on unload
  const finalSaveIfNeeded = useRef(null);
  finalSaveIfNeeded.current = () => {
    const currentSerialized = JSON.stringify(rows);
    if (currentSerialized === lastSavedRowsRef.current) return;
    const payload = buildSavePayload();
    const url = `${process.env.REACT_APP_AMS_BASE_URL}saveOrUpdateCandidateMarks`;
    const data = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([data], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, false); // synchronous fallback
        xhr.setRequestHeader("Content-Type", "application/json");
        try { xhr.send(data); } catch { }
      }
      lastSavedRowsRef.current = currentSerialized;
    } catch (e) {
      console.error("Final save failed", e);
    }
  };

  // beforeunload (desktop browsers)
  useEffect(() => {
    const onBeforeUnload = () => {
      finalSaveIfNeeded.current && finalSaveIfNeeded.current();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [rows]);

  // pagehide + visibilitychange (covers mobile/tab lifecycle better)
  useEffect(() => {
    const onPageHide = () => {
      finalSaveIfNeeded.current && finalSaveIfNeeded.current();
    };
    const onVisChange = () => {
      if (document.visibilityState === "hidden") {
        finalSaveIfNeeded.current && finalSaveIfNeeded.current();
      }
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisChange);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisChange);
    };
  }, [rows]);
  // Toast success icon
  const SuccessIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="10" fill="#FFFFFF" stroke="#2B8000" strokeWidth="2" />
      <path
        d="M8 12.5l2.5 2.5L16 9.5"
        fill="none"
        stroke="#2B8000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div className={styles.marksentrypage}>
      <div className={styles.xcontainer}>
        <section className="section mt-0 py-0" style={{ paddingRight: "0rem !important" }}>
          <div className="pt-3 subject-header-fixed">
            <p className="is-size-6 has-text-weight-bold">
              {getQualificationTitle(subjects, subjectData?.selectedSubjectId)}
            </p>
            <h1 className="title is-2 mb-0">
              {selectedSubject}{" "}
              <span className="is-size-6 has-text-weight-normal">
                {selectedSubjectCode}
              </span>
            </h1>
            <p className="xbox xnotification is-info mt-0">
              Update marks submission
            </p>
          </div>
        </section>
      </div>
      <div style={{ paddingLeft: "25px" }}>
        <MarkSubmissionHeader
         onSubmitClicked={handleOcrSubmit}
          showModal={showModal}
          setShowModal={setShowModal}
          rows={rows}
          isRowComplete={isRowComplete}
          onManualSaveSuccess={(savedRows) => {
            lastSavedRowsRef.current = JSON.stringify(savedRows);
            setLastSavedAt(new Date());
            if (autoSaveTimeoutRef.current) {
              clearTimeout(autoSaveTimeoutRef.current);
              autoSaveTimeoutRef.current = null;
            }
            // Also persist the successful save to localStorage
            if (STORAGE_KEY) {
              try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(savedRows));
              } catch {}
            }
          }}
          lastSavedAt={lastSavedAt}
          isStage3={isStage3}
          isPostStage3={isPostStage3}
          hasUnsavedChanges={hasUnsavedChanges}
          STORAGE_KEY={STORAGE_KEY}
        />
        <div className="notification is-info is-flex">
          <span className="icon mr-4">
            <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24">
              <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path>
            </svg>
          </span>
          <span>Enter updated marks for all candidates, except those who were absent or part of the original sample.</span>
        </div>
        <Toaster
          position="top-right"
          containerStyle={{ marginTop: "8rem" }}
          toastOptions={{
            style: {
              width: "336px",
              borderRadius: "4px",
              padding: "1em",
              fontFamily:
                "'Open Sans', 'Source Sans 3', Arial, Helvetica, sans-serif",
              boxShadow:
                "rgba(0, 0, 0, 0.14) 0px 8px 12px -4px, rgba(0, 0, 0, 0.24) 0px 4px 6px -2px",
            },
            success: {
              style: {
                backgroundColor: "#f5faf5",
                border: "1px solid #2B8000",
                color: "#2B8000",
              },
              icon: <SuccessIcon />,
            },
          }}
        />
        <UpdateMarkSubmissionTable
          rows={rows}
          setRows={setRows}
          availableAssignments={availableAssignments}
         isPostStage3={isPostStage3}
          showError={errorVisible}
          candidates={candidates}
          anyRowInvalid={anyRowInvalid}
           isRowComplete={isRowComplete}
        />
      </div>
    </div>
  )
}

export default UpdateMarksEntryPage;